// Shared types between the PDF editor UI and its MuPDF worker.
//
// The editor is deliberately built on MuPDF (Artifex's wasm build, the
// `mupdf` npm package) rather than the pdf.js + pdf-lib pair the rest
// of /apps/pdf-tools uses. MuPDF is a full PDF *editor* engine: it
// renders, exposes glyph-accurate structured text (which powers text
// selection → highlight quads), and writes real PDF annotation objects
// (/Annots with generated appearance streams) that remain editable in
// Acrobat & friends — not flattened page content. It also already
// supports the features later phases need: form fields, redaction,
// page manipulation, incremental save.
//
// Coordinate system: everything in this protocol is in MuPDF "fitz"
// page space — PDF points (1/72 in), origin at the page's top-left,
// y down, after page /Rotate is applied. That is exactly CSS pixel
// space at zoom 1, so the UI stores geometry unchanged and only
// multiplies by the current zoom for display. The worker converts to
// raw PDF space internally when writing annotations.

/** 8 numbers: ul.x, ul.y, ur.x, ur.y, ll.x, ll.y, lr.x, lr.y */
export type Quad = [
  number, number, number, number,
  number, number, number, number,
];

export interface PageInfo {
  /** Page size in PDF points at zoom 1 (after /Rotate). */
  width: number;
  height: number;
}

// ─────────────────────────── form widgets ───────────────────────────
//
// AcroForm fields are part of the document (not the annotation
// overlay), so widget *definitions* travel with the baseline — the
// worker enumerates them on open/authenticate/pageOp. Field *values*
// the user types are UI state: a serializable name → value map the
// worker writes into a fresh copy of the baseline at save time, the
// same way annotations are applied.

export interface WidgetInfo {
  /** Fully-qualified field name — the identity values are keyed by.
   *  Radio buttons in one group share it. */
  name: string;
  type: "text" | "checkbox" | "radio" | "combo" | "list" | "sig";
  pageIndex: number;
  /** x0, y0, x1, y1 in fitz page space. */
  rect: [number, number, number, number];
  /** Value currently stored in the document. */
  value: string;
  /** combo/list choices. */
  options?: string[];
  readOnly: boolean;
  maxLen?: number;
  multiline?: boolean;
  password?: boolean;
  /** Effective appearance font size in PDF points (text fields). */
  fontSize?: number;
  /** checkbox/radio: the field value that turns THIS widget on. */
  onValue?: string;
}

/** User-entered field values, keyed by fully-qualified field name. */
export type FormValues = Record<string, string>;

export interface OpenResult {
  pages: PageInfo[];
  widgets: WidgetInfo[];
  /** True when the file is password-protected; the UI must call
   *  `authenticate` before any other op will see the pages. */
  needsPassword: boolean;
}

export interface AuthenticateResult {
  ok: boolean;
  /** Populated when ok — same shape as OpenResult. */
  pages: PageInfo[];
  widgets: WidgetInfo[];
}

export interface RenderResult {
  width: number;
  height: number;
  /** RGBA, width * height * 4 bytes, transferred. */
  pixels: ArrayBuffer;
}

export interface SelectionResult {
  quads: Quad[];
  text: string;
}

// ─────────────────────────── annotations ───────────────────────────
//
// The UI owns the annotation list (plain serializable objects — this
// is what makes undo/redo trivial snapshots). The worker turns the
// list into real PDF annotation objects at save time, starting from
// the pristine original bytes each save so edits never accumulate.

/** One reply in an annotation's comment thread. Exported as a Text
 *  annotation with /IRT pointing at the parent (the standard Acrobat
 *  reply mechanism); imported threads collapse back into this list. */
export interface AnnotationReply {
  author?: string;
  text: string;
  /** ISO timestamp (converted to a PDF /M date at save). */
  date?: string;
}

/** Review status, exported as a hidden /IRT Text annotation with
 *  /StateModel Review — the convention Acrobat's comment pane reads. */
export type AnnotationStatus = "accepted" | "rejected" | "completed" | "cancelled";

/** Comment metadata shared by every comment-capable annotation kind. */
export interface AnnotationMeta {
  /** The annotation's own words (/Contents at save).
   *
   *  For a note or a text box the visible `value` IS the contents, and
   *  this is unused. For a highlight, drawing, shape or stamp there is
   *  nothing to read but the mark itself — so this is where the comment
   *  goes, and it is also the only thing a screen reader can announce
   *  about them. Replies are a thread ON this, not a substitute for it. */
  contents?: string;
  /** Comment author (/T at save; from the editor's author setting). */
  author?: string;
  /** ISO timestamp of creation/last edit (/M at save). */
  modified?: string;
  replies?: AnnotationReply[];
  status?: AnnotationStatus;
}

/** Text-markup annotations — all share the quad-based geometry the
 *  engine computes from a text selection; only the PDF subtype (and
 *  its visual treatment) differs. */
export type MarkupKind = "highlight" | "underline" | "strikeout" | "squiggly";

export interface MarkupAnnotation extends AnnotationMeta {
  id: string;
  kind: MarkupKind;
  pageIndex: number;
  /** CSS hex colour, e.g. "#ffe066". */
  color: string;
  /** One quad per selected text fragment, fitz page space. */
  quads: Quad[];
}

/** Text-box paragraph alignment (/Q at save). */
export type TextAlign = "left" | "center" | "right";

export interface TextAnnotation extends AnnotationMeta {
  id: string;
  kind: "text";
  pageIndex: number;
  /** Top-left of the text box, fitz page space. */
  x: number;
  y: number;
  /** Box size (measured from the rendered DOM, zoom-normalised). */
  width: number;
  height: number;
  /** Font size in PDF points. */
  size: number;
  /** CSS hex colour. */
  color: string;
  value: string;
  /** Typeface. Absent = "helv". "helv" | "times" | "cour" export as the
   *  Base-14 faces (no bytes to embed, so they always work); "lib:<id>"
   *  and "upload:<id>" are session faces the worker embeds from its
   *  registry (see registerFont) — if the registry doesn't have the
   *  face at save time (e.g. a restored session), the box falls back to
   *  Helvetica rather than failing the export. */
  font?: string;
  /** Absent = regular. mupdf's own FreeText appearance generator cannot
   *  do either (every Base-14 variant name collapses to the regular
   *  face — measured), so a bold or italic box is drawn by the
   *  hand-written appearance path like a custom face is. */
  bold?: boolean;
  italic?: boolean;
  /** Clockwise degrees. A FreeText's /Rect is axis-aligned by
   *  definition, so rotation lives in the appearance stream's matrix and
   *  the rect becomes the rotated bounding box. */
  rotation?: number;
  /** Which CJK ordering to draw Han ideographs with. Only consulted when
   *  the text HAS Han and no kana or hangul to give it away — the same
   *  characters are written in all four scripts, so this is a genuine
   *  choice rather than something to detect. Absent = Simplified
   *  Chinese, the ordering with the widest Han coverage. */
  cjkScript?: "zh-Hans" | "zh-Hant" | "ja" | "ko";
  /** Absent = left. */
  align?: TextAlign;
  /** 0..1; absent = opaque (/CA at save). */
  opacity?: number;
}

export interface InkAnnotation extends AnnotationMeta {
  id: string;
  kind: "ink";
  pageIndex: number;
  /** CSS hex colour (stroke). */
  color: string;
  /** Stroke width in PDF points. */
  strokeWidth: number;
  /** One freehand stroke: flat [x0, y0, x1, y1, …], fitz page space. */
  points: number[];
}

export type ShapeKind = "rect" | "ellipse" | "line" | "arrow";

/** Stroke dash pattern for shapes. */
export type DashStyle = "solid" | "dashed" | "dotted";

/** Line-end decorations (line/arrow shapes). */
export type HeadStyle = "none" | "arrow" | "open" | "circle" | "square" | "diamond";

export interface ShapeAnnotation extends AnnotationMeta {
  id: string;
  kind: "shape";
  shape: ShapeKind;
  pageIndex: number;
  /** Drag start / end, fitz page space. rect & ellipse treat the two
   *  points as opposite bounding-box corners; line & arrow as their
   *  endpoints (arrowhead at p2). */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** CSS hex colour (stroke). */
  color: string;
  /** Stroke width in PDF points. */
  strokeWidth: number;
  /** CSS hex fill for rect/ellipse, or null for stroke-only. */
  fill: string | null;
  /** Absent = solid (pre-existing annotations). */
  dash?: DashStyle;
  /** Line/arrow end decorations. Absent = legacy default: end "arrow"
   *  when shape === "arrow", otherwise none. */
  headStart?: HeadStyle;
  headEnd?: HeadStyle;
  /** Clockwise degrees, for rect/ellipse only — a line or arrow rotates
   *  by moving its endpoints, which needs no angle. */
  rotation?: number;
}

/** Effective end decorations with the legacy default applied. */
export function shapeHeads(a: ShapeAnnotation): { start: HeadStyle; end: HeadStyle } {
  return {
    start: a.headStart ?? "none",
    end: a.headEnd ?? (a.shape === "arrow" ? "arrow" : "none"),
  };
}

export interface NoteAnnotation extends AnnotationMeta {
  id: string;
  kind: "note";
  pageIndex: number;
  /** Top-left of the note icon, fitz page space. */
  x: number;
  y: number;
  /** CSS hex colour of the icon. */
  color: string;
  /** Popup/comment contents. */
  value: string;
}

export interface StampAnnotation extends AnnotationMeta {
  id: string;
  kind: "stamp";
  pageIndex: number;
  /** Top-left + size, fitz page space. Aspect ratio locked to the image. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** PNG/JPEG image as a data URL — serializable (IndexedDB session)
   *  and directly renderable in the overlay; the worker decodes it at
   *  save time into the Stamp annotation's appearance. */
  dataUrl: string;
  /** Clockwise degrees, applied by the appearance matrix. */
  rotation?: number;
}

/** A pending redaction. In the editor it's a reversible overlay like
 *  any other annotation; at save time the worker creates real Redact
 *  annotations and APPLIES them — the covered text/images are removed
 *  from the exported file's content, not just hidden, and the file is
 *  fully rewritten (no incremental save) so no trace survives. */
export interface RedactAnnotation {
  id: string;
  kind: "redact";
  pageIndex: number;
  /** Glyph quads from a text selection, or one rect-as-quad for an
   *  area redaction. */
  quads: Quad[];
}

/** A clickable link region. Existing document links import into this
 *  model (stripped from the doc like other imported annotations) and
 *  every link is re-created as a real /Annots Link at save — AFTER any
 *  flatten bake, so links survive "flatten everything". Exactly one of
 *  `url` (external) / `page` (internal, 0-based) is set. */
export interface LinkAnnotation {
  id: string;
  kind: "link";
  pageIndex: number;
  /** Clickable area, fitz page space. */
  rect: [number, number, number, number];
  url?: string;
  page?: number;
}

export type Annotation =
  | MarkupAnnotation
  | TextAnnotation
  | InkAnnotation
  | ShapeAnnotation
  | NoteAnnotation
  | StampAnnotation
  | RedactAnnotation
  | LinkAnnotation;

/** Rendered size of the sticky-note icon, PDF points. */
export const NOTE_SIZE = 20;

export function isMarkup(a: Annotation): a is MarkupAnnotation {
  return (
    a.kind === "highlight" ||
    a.kind === "underline" ||
    a.kind === "strikeout" ||
    a.kind === "squiggly"
  );
}

// ─────────────────────────── page operations ───────────────────────────
//
// Page ops mutate the document itself, not the annotation overlay — so
// the worker applies them to the current doc, saves, and re-opens the
// result as the NEW baseline ("original bytes"). The UI stores that
// baseline in its undo history alongside the (index-remapped)
// annotation list, which is what makes page ops undoable.

export type PageOp =
  | { kind: "rotate"; pageIndex: number; clockwise: boolean }
  /** Trim the visible page to `rect` (fitz view space of the page the
   *  user dragged on, clamped per page); null restores the full page.
   *  Non-destructive — it sets /CropBox, it does not delete content. */
  | { kind: "crop"; target: number | "all"; rect: [number, number, number, number] | null }
  | { kind: "delete"; pageIndex: number }
  | { kind: "move"; from: number; to: number }
  | { kind: "insertBlank"; at: number; width: number; height: number }
  /** Page size / orientation / margins. The page box becomes
   *  width×height (view-space points); content is wrapped in a
   *  transform that scales it into the margin-inset box ("scale") or
   *  keeps its size centred there ("center"). Margins are per-side in
   *  view space (top = visual top of the page). Annotation/widget
   *  geometry is transformed with the same matrix. */
  | {
      kind: "geometry";
      target: number | "all";
      width: number;
      height: number;
      margins: PageMargins;
      fit: "scale" | "center";
    };

export interface PageMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** View-space scale + offset the geometry op applies to one page. */
export function geometryTransform(
  oldW: number,
  oldH: number,
  op: { width: number; height: number; margins: PageMargins; fit: "scale" | "center" }
): { s: number; tx: number; ty: number } {
  const { top, right, bottom, left } = op.margins;
  const innerW = Math.max(8, op.width - left - right);
  const innerH = Math.max(8, op.height - top - bottom);
  const s =
    op.fit === "scale" ? Math.max(0.02, Math.min(innerW / oldW, innerH / oldH)) : 1;
  return {
    s,
    tx: left + (innerW - s * oldW) / 2,
    ty: top + (innerH - s * oldH) / 2,
  };
}

// ─────────────────────────── content editing ───────────────────────────
//
// Text editing is erase-and-replace: the worker locates the clicked
// structured-text line (glyph quads + baseline + font traits), the UI
// collects a replacement, and `editContent` redacts the original glyphs
// (no black box) and appends a content-stream fragment drawing the new
// text — then rebases, so the edit is undoable via baseline history.
// Fonts are matched to the Base-14 set; original-font reuse is a later
// stage. Clicking an image offers deletion via the same erase path.

export type TextHit =
  | {
      kind: "line";
      /** Line text — or, in block mode, the paragraph joined with
       *  single spaces (hard newlines only between stext blocks'
       *  original hard breaks the user re-introduces). */
      text: string;
      /** Per-glyph quads (the erase region), fitz page space. */
      quads: Quad[];
      bbox: [number, number, number, number];
      /** Baseline origin of the first glyph. */
      origin: [number, number];
      /** Font size in points (first glyph's). */
      size: number;
      /** Matched Base-14 face for the replacement text. */
      base14: string;
      /** The document's own font name, for display. */
      rawFont: string;
      /** CSS hex of the first glyph. */
      color: string;
      /** The embedded font file, when the document has one — the edit
       *  overlay loads it via the FontFace API so typing happens in the
       *  REAL face (per-glyph fallback covers subset gaps). */
      fontBytes: Uint8Array | null;
      /** Present in block mode: layout facts for re-wrapping. */
      block?: { lineHeight: number; maxWidth: number; lineCount: number };
    }
  | { kind: "image"; bbox: [number, number, number, number] }
  | { kind: "fill"; fill: FillInfo }
  | { kind: "none" };

// ─────────────────────── vector fills (colour blocks) ───────────────────────
//
// Solid blocks behind content — banners, table shading, cards — are
// fill (or stroke) paths in the content stream. The worker enumerates
// them with a JS device sweep; each becomes a selectable object. Edits
// are erase-and-redraw: line-art redaction removes the path (text and
// images survive), then the exact path operators are replayed under an
// adjusted matrix/colour — PREPENDED so the redraw stays behind the
// page's text, or appended + affected text redrawn on top when an
// earlier surviving fill overlaps (stacked backgrounds).

export interface FillInfo {
  /** Stable within one baseline generation ("f<paintIndex>[.<sub>]"). */
  id: string;
  /** Fitz page space; for strokes, padded by half the line width. */
  bbox: [number, number, number, number];
  /** CSS hex (DeviceRGB approximation for exotic colour spaces). */
  color: string;
  alpha: number;
  /** A stroked outline (border) rather than a filled area. */
  stroke: boolean;
  /** Axis-aligned rectangle — move/resize supported. */
  rect: boolean;
  /** One rectangle of a multi-rectangle path (e.g. table stripes) —
   *  editing it rewrites the whole path with this piece changed. */
  partOfGroup: boolean;
  /** Recolour/move faithful (false for clip-shaped fills — delete only). */
  recolorable: boolean;
  /** Gradient (shading). Selectable/editable only when `gradientMeta`
   *  was resolved (the `sh` operator was located in the page stream). */
  gradient?: boolean;
  gradientMeta?: {
    /** Paint operator located — delete works. */
    located: boolean;
    /** Axial/radial (type 2/3) — reverse works. */
    reversible: boolean;
    radial: boolean;
    /** Start/end stop colours (DeviceRGB approximation). */
    start: string;
    end: string;
    /** Exponential (or stitched-exponential) function — the stop
     *  colours can be rewritten. */
    stopsEditable: boolean;
  };
}

export type FillAction =
  | { kind: "delete" }
  /** Move/resize: redraw at `to` (view-space bbox). Rect fills only. */
  | { kind: "move"; to: [number, number, number, number] }
  | { kind: "recolor"; color: string }
  /** Insert a NEW block behind existing content (id is ignored). */
  | { kind: "add"; bbox: [number, number, number, number]; color: string }
  /** Change paint depth: erase + redraw as the LAST content stream
   *  (front: paints above everything, text included — deliberate) or
   *  the FIRST (back: behind all other content). Same replayability
   *  requirement as recolour. */
  | { kind: "restack"; front: boolean }
  /** Gradient stop colours (start = C0 of the first function, end = C1
   *  of the last). NOTE: edits the shared Shading resource — every use
   *  of it changes, like editing a named swatch. */
  | { kind: "gradientStops"; start?: string; end?: string }
  /** Swap an axial/radial gradient's direction (Coords + odd Extend). */
  | { kind: "gradientReverse" };

// ─────────────── page furniture (headers / footers / watermark) ───────────────
//
// Burned into the page content streams (one rebase → undoable like any
// page op). Header/footer slots substitute {page}, {pages} and {date}
// per page; the watermark is centred rotated text — normally appended
// on top with low opacity, optionally prepended behind content (which
// full-bleed background fills can hide).

// ──────────────────── region select (multi-object) ────────────────────
//
// Marquee-select whatever a rectangle covers — text lines, images and
// vector fills — then move the lot as ONE operation (one erase pass,
// one redraw, one rebase → one undo step). Ids are positional within
/** One page object as the Objects panel lists it. Vectors keep their
 *  true paint order; text and images are appended in reading order
 *  (see pageObjects — a full interleaved z-order sweep is future work). */
export interface ZObject {
  id: string;
  /** Paint order — higher paints later, i.e. on top. */
  index: number;
  kind: "text" | "image" | "fill" | "stroke" | "shade";
  bbox: [number, number, number, number];
  label: string;
  text?: string;
  color?: string;
  alpha?: number;
  size?: number;
  /** Matches a collectFills id, so vector rows can use the fill ops. */
  fillId?: string;
}

export interface DocFontInfo {
  /** Display name (subset prefix stripped). */
  name: string;
  rawName: string;
  /** PDF font subtype: Type1 / TrueType / Type0 / Type3 … */
  type: string;
  /** The font file travels with the document. */
  embedded: boolean;
  /** Subset-embedded (ABCDEF+Name) — only the glyphs actually used. */
  subset: boolean;
  /** 1-based pages that reference it. */
  pages: number[];
}

// ─────────────── find & redact ───────────────

/** A compiled pattern for the whole-document scan (see
 *  lib/redact-patterns.ts, which builds these). */
export interface ScanPattern {
  id: string;
  source: string;
  flags: string;
}

/** One match, with the glyph quads a redaction mark needs. */
export interface ScanHit {
  pageIndex: number;
  patternId: string;
  /** The matched text — shown in the review list. */
  text: string;
  /** Surrounding line text, for context in the list. */
  context: string;
  quads: Quad[];
}

// ─────────────── document properties & security ───────────────

/** The Info-dictionary fields a user can edit. Everything here is a
 *  plain string; an empty one means "remove this key". */
export interface DocInfoFields {
  title: string;
  author: string;
  subject: string;
  keywords: string;
  creator: string;
  producer: string;
}

/** What viewers are asked to allow on an encrypted document. These only
 *  have meaning when the file is encrypted. */
export interface DocPermissions {
  print: boolean;
  /** High-resolution printing (bit 12); when off, viewers print a
   *  degraded raster. Implied off when `print` is off. */
  printHq: boolean;
  copy: boolean;
  edit: boolean;
  annotate: boolean;
  form: boolean;
  accessibility: boolean;
  assemble: boolean;
}

export interface DocInfo extends DocInfoFields {
  /** Raw PDF date strings (D:YYYYMMDDHHmmSS…), as stored. */
  creationDate: string;
  modDate: string;
  /** "PDF 1.7" */
  format: string;
  /** "None" or e.g. "Standard V5 R6 256-bit AES". */
  encryption: string;
  /** The document carries an XMP packet mirroring these fields. */
  hasXmp: boolean;
  /** What the CURRENTLY OPEN file permits (all true when unencrypted). */
  permissions: DocPermissions;
  pageCount: number;
}

/** How an export should be encrypted. `keep` reproduces whatever the
 *  source file had (mupdf's default), `none` strips encryption, and
 *  `encrypt` writes fresh AES-256 with these passwords + permissions.
 *  An empty `userPassword` with an owner password is the "opens freely,
 *  restricted actions" shape. */
export interface SecurityOptions {
  mode: "keep" | "none" | "encrypt";
  userPassword?: string;
  ownerPassword?: string;
  permissions?: DocPermissions;
}

// the current baseline: select and move must run against the same
// generation, which the UI's op queue guarantees.

/** One selectable object on a page. Content kinds are positional in the
 *  CURRENT baseline and are resolved by the worker; `annot` (overlay)
 *  and `widget` (form field) items are built by the UI, which already
 *  holds both lists. A selection may mix all five. */
export interface RegionItem {
  /** "t:<lineIndex>" | "i:<imageIndex>" | "f:<fillId>"
   *  | "w:<pageWidgetIndex>" | "a:<annotId>" */
  id: string;
  kind: "text" | "image" | "fill" | "widget" | "annot";
  bbox: [number, number, number, number];
}

/** One object's displacement — a drag sends the same delta for every
 *  item, align/distribute a different one each. */
export interface RegionMove {
  id: string;
  dx: number;
  dy: number;
}

// ────────────────────── form-field creation ──────────────────────
//
// Fields live in the document (AcroForm), not the annotation overlay,
// so creating one rebases like a page op — undoable, and the existing
// form-filling overlay picks it up from the refreshed widget list.

export interface FieldSpec {
  kind: "text" | "checkbox" | "combo" | "radio" | "signature";
  /** Field name. Unique for most kinds (the worker de-duplicates) —
   *  except radios, where a REPEATED name is the whole point: buttons
   *  sharing a name form one group, exactly as in Acrobat. Each drag
   *  adds one button; name it the same to add another choice. */
  name: string;
  /** Fitz view space. */
  rect: [number, number, number, number];
  /** Text fields only. */
  multiline?: boolean;
  /** Dropdown choices. */
  options?: string[];
  /** Radio only: the value this button stands for (its export value).
   *  Blank = "Option N". */
  onValue?: string;
  fontSize?: number;
}

// ────────────────────────── OCR text layer ──────────────────────────
//
// OCR runs on the MAIN thread (tesseract.js spawns its own worker,
// fed page rasters); the recognised words then go to the PDF worker,
// which draws them as INVISIBLE text (render mode 3) at the word
// boxes — one rebase for the whole batch, so a multi-page OCR is a
// single undo step and the text exports/searches/selects like any
// other. Word boxes are fitz view points.

// ─────────────── accessibility ───────────────

/** What the worker can tell about how readable this document is to
 *  assistive technology. Facts only — the judgement lives in
 *  lib/a11y.ts, which is why it can be unit-tested. */
export interface A11yFacts {
  pageCount: number;
  /** Info /Title. */
  title: string;
  /** Catalog /ViewerPreferences /DisplayDocTitle. */
  displayDocTitle: boolean;
  /** Catalog /Lang ("" when absent). */
  lang: string;
  /** /MarkInfo /Marked true. */
  marked: boolean;
  /** A /StructTreeRoot exists. */
  hasStructTree: boolean;
  encrypted: boolean;
  /** /P bit 10 — may a screen reader extract the text? */
  extractForAccessibility: boolean;
  /** 1-based pages with no extractable text at all. */
  pagesWithoutText: number[];
  /** 1-based pages that draw images but extract no text. */
  imageOnlyPages: number[];
  imageCount: number;
  /** Figure elements in the structure tree, and how many carry /Alt.
   *  Only meaningful once the document is tagged — an untagged file has
   *  nowhere to put alternate text at all. */
  figureCount: number;
  figuresWithAlt: number;
  widgetCount: number;
  /** Fully-qualified names of form fields with no /TU description. */
  fieldsWithoutDescription: string[];
  /** 1-based pages holding more than one field with no /Tabs entry. */
  pagesWithoutTabOrder: number[];
  outlineCount: number;
  /** Fonts with neither /ToUnicode nor a standard encoding. */
  fontsWithoutUnicode: string[];
  /** Annotations already in the file that carry no /Contents. */
  docAnnotsWithoutDescription: number;
}

/** The repairs `setAccessibility` can make. Each is a flag in the file,
 *  not a content decision — tagging and alternate text are deliberately
 *  absent because no tool can write them honestly. */
export interface A11yPatch {
  /** BCP-47 tag for the catalog /Lang. */
  lang?: string;
  /** Catalog /ViewerPreferences /DisplayDocTitle. */
  displayDocTitle?: boolean;
  /** Give every page of form fields a /Tabs /S (structure order). */
  tabOrder?: boolean;
  /** Seed each undescribed field's /TU from its own name. */
  fieldDescriptions?: boolean;
}

export interface OcrWord {
  text: string;
  bbox: [number, number, number, number];
}

// ─────────────────────── bookmarks (document outline) ───────────────────────
//
// The outline lives in the document itself, so the UI reads it fresh
// per baseline generation and writes the WHOLE tree back: the worker
// clears the existing outline and rebuilds it depth-first, then rebases
// — which makes bookmark edits undoable and exported for free.

export interface OutlineItem {
  title: string;
  /** 0-based target page (unresolvable/external targets clamp to 0). */
  page: number;
  /** Nesting depth, 0 = top level. Never skips a level. */
  level: number;
  /** Expanded when the document opens. */
  open: boolean;
}

export type DecorTarget = "all" | "notFirst" | number;

export interface HeaderFooterConfig {
  /** header left/centre/right, footer left/centre/right. Empty = skip. */
  slots: { hl: string; hc: string; hr: string; fl: string; fc: string; fr: string };
  /** Library/uploaded face (embedded); absent = Helvetica. */
  customFont?: { name: string; bytes: ArrayBuffer };
  size: number;
  color: string;
  /** Distance from the side edges (pt). */
  marginX: number;
  /** Baseline distance from the top/bottom edge (pt). */
  marginY: number;
  target: DecorTarget;
}

export interface WatermarkConfig {
  text: string;
  /** Library/uploaded face (embedded); absent = Helvetica. */
  customFont?: { name: string; bytes: ArrayBuffer };
  size: number;
  color: string;
  /** 0..1 fill/stroke alpha. */
  opacity: number;
  /** Visual counter-clockwise degrees (45 = rising diagonal). */
  angleDeg: number;
  /** Paint under the page content instead of over it. */
  behind: boolean;
  target: DecorTarget;
}

export interface ContentDraw {
  text: string;
  /** Baseline origin, fitz page space. */
  x: number;
  y: number;
  size: number;
  /** Base-14 fallback font name (e.g. "Helvetica-Bold"). */
  font: string;
  /** The document's own font name (as reported by textHit) — when its
   *  embedded font file exists and covers every replacement glyph, the
   *  worker reuses it instead of the Base-14 fallback. */
  rawFont?: string;
  /** CSS hex. */
  color: string;
  /** CSS hex — a filled rectangle drawn behind the text (highlighted
   *  label look). Null/absent = none. */
  background?: string | null;
  /** A library/uploaded face to embed and draw with — takes precedence
   *  over `font`/`rawFont`. Bytes are a TTF/OTF file. */
  customFont?: { name: string; bytes: ArrayBuffer };
  /** Paragraph mode: re-wrap `text` to maxWidth (points, measured with
   *  the actual font) and advance baselines by lineHeight per line.
   *  Hard newlines in `text` force breaks. */
  wrap?: { maxWidth: number; lineHeight: number };
}

export interface ContentErase {
  quads: Quad[];
  /** What the erase targets — glyphs or an image. */
  what: "text" | "image";
}

/** Manipulate a content image located by its fitz-space bbox. "move"
 *  redraws the SAME image at `to` (which also implements resize);
 *  "replace" draws the supplied image into the original box. */
export type ImageAction =
  | { kind: "delete" }
  | { kind: "move"; to: [number, number, number, number] }
  | { kind: "replace"; dataUrl: string };

// ─────────────────────── markdown import ───────────────────────
//
// Import flow: the UI lexes the markdown (marked), pre-resolves the
// theme's fonts (Base-14 names / library-font bytes) and any images
// (data URL + natural size), then `importMd` lays the document out
// (md-layout.ts) and builds a SCRATCH PDF the worker holds alongside
// the open document — like compare mode, it survives nothing and
// touches nothing. `importMdRender` previews its pages; every theme
// tweak just re-runs `importMd`. `importMdCommit` saves the scratch
// doc and returns the bytes (the preview IS the final PDF — same
// bytes), which the UI opens as a new tab. `importMdClose` discards.

/** One resolved font face the generated document may use.
 *  `bytes` present → embed (library/uploaded face); absent → `name`
 *  is a Base-14 PostScript name (Helvetica-Bold, Times-Roman, …). */
export interface MdFace {
  /** faceKey(family, bold, italic) — what layout ops reference. */
  key: string;
  name: string;
  bytes?: ArrayBuffer;
}

/** An image the UI managed to decode (data URL + natural CSS px). */
export interface MdImageResolved {
  dataUrl: string;
  width: number;
  height: number;
}

// ─────────────────────────── RPC envelope ───────────────────────────

export interface WorkerRequestMap {
  /** Create a fresh single-page PDF (dimensions in points). */
  blank: { width: number; height: number };
  open: { bytes: ArrayBuffer };
  /** Unlock the currently open password-protected document. */
  authenticate: { password: string };
  render: { pageIndex: number; scale: number };
  selection: {
    pageIndex: number;
    p: [number, number];
    q: [number, number];
  };
  /** Find `needle` on one page; one Quad[] per hit. */
  search: { pageIndex: number; needle: string; maxHits: number };
  /** Structure boxes (blocks/lines/images) for the inspector overlay. */
  pageObjects: { pageIndex: number };
  /** Apply a page operation and rebase: the returned bytes become the
   *  document the worker has open (and the caller's new baseline). */
  pageOp: { op: PageOp };
  /** Convert the document's pre-existing annotations (of kinds this
   *  editor supports) into overlay annotations, stripping them from
   *  the document so they aren't drawn twice. When anything was
   *  imported, `bytes` is the stripped rebase (else null). */
  importAnnots: Record<string, never>;
  /** Find the text line / paragraph block (or image) under a point. */
  textHit: { pageIndex: number; x: number; y: number; mode: "line" | "block" };
  /** Erase-and/or-draw page content, then rebase (like pageOp). */
  editContent: { pageIndex: number; erase: ContentErase | null; draw: ContentDraw | null };
  /** Find & replace by redrawing affected lines; one rebase. `only`
   *  limits it to the single occurrence overlapping that hit quad. */
  replaceText: {
    needle: string;
    replacement: string;
    only?: { pageIndex: number; quad: Quad };
  };
  /** Delete / move / replace a content image, then rebase. */
  imageOp: {
    pageIndex: number;
    bbox: [number, number, number, number];
    action: ImageAction;
  };
  /** Delete / move / recolour a vector fill (or add a new block),
   *  then rebase. `id` comes from a textHit fill result. */
  fillOp: { pageIndex: number; id: string; action: FillAction };
  /** Burn headers/footers/page numbers and/or a watermark into the
   *  target pages' content, then rebase. */
  decorate: { headerFooter?: HeaderFooterConfig; watermark?: WatermarkConfig };
  /** Open a SECOND document for compare mode (kept until compareClose;
   *  survives rebases — it's independent of the editing document). */
  compareOpen: { bytes: ArrayBuffer };
  /** Render a page of the compare document. */
  compareRender: { pageIndex: number; scale: number };
  /** Image export: open a (flattened) copy in its own read-only slot,
   *  rasterise its pages, then let it go. Separate from the compare slot
   *  so exporting doesn't evict a comparison in progress. */
  exportOpen: { bytes: ArrayBuffer };
  exportRender: { pageIndex: number; scale: number };
  exportClose: Record<string, never>;
  /** Reduce file size. `baseline` is the PDF to work on (the caller
   *  saves first, so annotations and form values are included), and
   *  `images` opts in to the lossy image pass. */
  optimize: {
    baseline: ArrayBuffer;
    images: { maxDpi: number; quality: number } | null;
  };
  compareClose: Record<string, never>;
  /** Every font the document references (usage + embedding). */
  documentFonts: Record<string, never>;
  /** Run patterns over every page's text and return the matches with
   *  their glyph quads (find & redact). Read-only — the caller decides
   *  which hits become redaction marks. */
  scanText: { patterns: ScanPattern[]; maxHits: number };
  /** Info dictionary, format, encryption state and permissions. */
  documentInfo: Record<string, never>;
  /** Add a face to the worker's session font registry, so text boxes
   *  using it can be embedded at save time. Bytes are kept for the
   *  session and survive document switches and rebases. */
  registerFont: { id: string; name: string; bytes: ArrayBuffer };
  accessibilityFacts: Record<string, never>;
  setAccessibility: { patch: A11yPatch };
  /** Write the Info dictionary (and reconcile the XMP packet), then
   *  rebase — so it undoes like any other document edit. Omitted keys
   *  are left alone; empty strings delete the key. */
  setDocumentInfo: { fields: Partial<DocInfoFields> };
  /** Objects (text lines / images / fills) inside a marquee rect. */
  regionSelect: { pageIndex: number; rect: [number, number, number, number] };
  /** The single object under a point — same id scheme as regionSelect,
   *  so hits can be added to a region selection and moved with it.
   *  `kind` restricts the search (used to re-resolve an already-selected
   *  image whose id the UI doesn't carry). */
  regionHit: {
    pageIndex: number;
    x: number;
    y: number;
    /** Content kinds only — the worker doesn't know about the overlay. */
    kind?: "text" | "image" | "fill";
  };
  /** Displace every listed object, then rebase. */
  regionMove: { pageIndex: number; moves: RegionMove[] };
  /** Erase every listed object (content) / remove it from the form
   *  (widgets), then rebase. */
  regionDelete: { pageIndex: number; ids: string[] };
  /** Add an interactive form field to a page, then rebase. */
  createField: { pageIndex: number; field: FieldSpec };
  /** Move (and optionally resize) an existing form field's widget.
   *  Identified by page + index within that page's widget list, because
   *  a NAME can belong to several widgets — a radio group is one field
   *  with a button per choice, and moving "the field called Delivery"
   *  would be ambiguous. */
  moveField: {
    pageIndex: number;
    widgetIndex: number;
    /** New box, fitz page space. */
    rect: [number, number, number, number];
  };
  /** Inspect a PDF the user picked for insertion WITHOUT touching the
   *  open document: page count plus small page thumbnails (capped —
   *  `pageCount` is always the true total). */
  probePdf: { bytes: ArrayBuffer; thumbWidth: number; maxThumbs: number };
  /** Graft a page range from another PDF into the open document at
   *  `at`, then rebase. `from`/`to` are 0-based inclusive source pages
   *  (clamped). NOTE: grafting carries page content and resources but
   *  NOT the source's annotations/links. */
  insertPages: { at: number; bytes: ArrayBuffer; from: number; to: number };
  /** Build a NEW standalone PDF from the given pages (in ascending
   *  order), with the overlay annotations applied live and the form
   *  values filled. Does NOT touch the open document (no rebase) —
   *  implemented as clone-and-delete-others so AcroForm fields and
   *  page resources survive (graftPage would drop widgets). */
  extractPages: {
    indices: number[];
    annotations: Annotation[];
    formValues: FormValues;
  };
  /** Delete several pages in ONE rebase (thumbnail batch delete). */
  deletePages: { indices: number[] };
  /** Draw recognised words as an invisible text layer, then rebase. */
  textLayer: { pages: { pageIndex: number; words: OcrWord[] }[] };
  /** Read the document outline as a flat, level-tagged list. */
  outline: Record<string, never>;
  /** Replace the whole outline with `items`, then rebase. */
  setOutline: { items: OutlineItem[] };
  /** Build the markdown-import scratch PDF (see section above).
   *  `tokens` is the marked token tree (plain serializable objects). */
  importMd: {
    tokens: unknown[];
    theme: import("./md-theme.ts").MdThemeConfig;
    faces: MdFace[];
    images: Record<string, MdImageResolved>;
  };
  /** Build the spreadsheet-import scratch PDF. Same lifecycle as
   *  importMd — the SAME render/commit/close messages serve both (the
   *  UI never runs two imports at once). `sheets` is parsed cell data
   *  (sheet-data.ts), `meta` feeds the {file}/{date} placeholders. */
  importSheet: {
    sheets: import("./sheet-layout.ts").SheetData[];
    theme: import("./sheet-theme.ts").SheetThemeConfig;
    faces: MdFace[];
    meta: import("./sheet-layout.ts").SheetLayoutMeta;
  };
  /** Build the image-import scratch PDF (photos → PDF). Same
   *  lifecycle as importMd; pages may differ in size. */
  importPhotos: {
    items: import("./photo-layout.ts").PhotoItem[];
    theme: import("./photo-theme.ts").PhotoThemeConfig;
    faces: MdFace[];
    /** Keyed by PhotoItem.id. */
    images: Record<string, MdImageResolved>;
    meta: import("./sheet-layout.ts").SheetLayoutMeta;
  };
  /** Render a page of the import scratch document. */
  importMdRender: { pageIndex: number; scale: number };
  /** Save the scratch document and discard it — the returned bytes
   *  are exactly what the preview showed. */
  importMdCommit: Record<string, never>;
  /** Discard the scratch document (cancel). */
  importMdClose: Record<string, never>;
  save: {
    annotations: Annotation[];
    formValues: FormValues;
    /** "forms" bakes form fields into page content; "all" additionally
     *  bakes every annotation (nothing stays editable). */
    flatten: "none" | "forms" | "all";
    /** Encryption + permissions for this export. Undefined means
     *  "keep": whatever encryption the source file had survives. */
    security?: SecurityOptions;
    /** Strip the document Info dictionary and XMP metadata (author,
     *  title, producer, creation dates…). */
    stripMetadata?: boolean;
  };
  prepareSign: {
    /** The document to sign, produced exactly as a download would be. */
    save: WorkerRequestMap["save"];
    field: SignatureFieldSpec;
  };
  close: Record<string, never>;
}

/** What goes in the signature dictionary and its widget. */
export interface SignatureFieldSpec {
  /** /Name — who signed. */
  name?: string;
  /** /Reason, /Location, /ContactInfo — the customary provenance trio. */
  reason?: string;
  location?: string;
  contactInfo?: string;
  /** ISO timestamp for /M. */
  signedAt: string;
  /** Where the visible stamp goes, or null for an invisible signature
   *  (a zero-size widget, which is both legal and common). */
  visible: { pageIndex: number; rect: [number, number, number, number] } | null;
}

export interface WorkerResponseMap {
  blank: { bytes: ArrayBuffer };
  open: OpenResult;
  authenticate: AuthenticateResult;
  render: RenderResult;
  selection: SelectionResult;
  search: { hits: Quad[][] };
  pageObjects: {
    blocks: [number, number, number, number][];
    lines: [number, number, number, number][];
    images: [number, number, number, number][];
    fills: { bbox: [number, number, number, number]; stroke: boolean; gradient: boolean }[];
  };
  pageOp: {
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    /** [pageIndex, dx, dy] view-space shifts the caller must apply to
     *  that page's annotations — cropping moves the page origin. */
    offsets?: [number, number, number][];
  };
  importAnnots: {
    annotations: Annotation[];
    bytes: ArrayBuffer | null;
    pages: PageInfo[];
    widgets: WidgetInfo[];
  };
  textHit: TextHit;
  editContent: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  replaceText: {
    count: number;
    /** Null when nothing was replaced (no rebase happened). */
    bytes: ArrayBuffer | null;
    pages: PageInfo[];
    widgets: WidgetInfo[];
  };
  imageOp: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  fillOp: {
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    /** The edited/added block relocated in the NEW baseline (for
     *  keeping it selected) — null when deleted or not re-found. */
    fill: FillInfo | null;
  };
  decorate: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  compareOpen: { pageCount: number; pages: PageInfo[] };
  exportOpen: { pageCount: number; pages: PageInfo[] };
  compareRender: RenderResult;
  compareClose: Record<string, never>;
  exportRender: RenderResult;
  exportClose: Record<string, never>;
  optimize: {
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    /** Size of the document handed in. */
    before: number;
    /** Size after both passes. */
    after: number;
    imagesRecompressed: number;
    imagesSeen: number;
    imageBytesSaved: number;
  };
  documentFonts: { fonts: DocFontInfo[] };
  scanText: { hits: ScanHit[]; truncated: boolean; pagesScanned: number };
  documentInfo: { info: DocInfo };
  registerFont: Record<string, never>;
  accessibilityFacts: { facts: A11yFacts };
  setAccessibility: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  setDocumentInfo: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  regionSelect: { items: RegionItem[] };
  regionHit: { item: RegionItem | null };
  regionMove: {
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    /** The moved objects re-resolved against the rebased document —
     *  their ids changed, so the caller must adopt these to stay
     *  movable. */
    items: RegionItem[];
  };
  regionDelete: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  moveField: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  createField: {
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    /** The name actually used (may be de-duplicated). */
    name: string;
  };
  probePdf: {
    pageCount: number;
    thumbs: { pageIndex: number; width: number; height: number; pixels: ArrayBuffer }[];
  };
  insertPages: {
    bytes: ArrayBuffer;
    pages: PageInfo[];
    widgets: WidgetInfo[];
    /** How many pages were actually inserted (drives annotation remap). */
    count: number;
  };
  extractPages: { bytes: ArrayBuffer };
  deletePages: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  textLayer: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  outline: { items: OutlineItem[] };
  setOutline: { bytes: ArrayBuffer; pages: PageInfo[]; widgets: WidgetInfo[] };
  importMd: { pages: PageInfo[]; missingImages: string[] };
  importSheet: { pages: PageInfo[] };
  importPhotos: { pages: PageInfo[] };
  importMdRender: RenderResult;
  importMdCommit: { bytes: ArrayBuffer };
  importMdClose: Record<string, never>;
  save: { bytes: ArrayBuffer };
  prepareSign: {
    /** The finished document with the signature dictionary in place, its
     *  /ByteRange already final and a hole where the signature goes. */
    bytes: ArrayBuffer;
    /** Byte span of that hole, delimiters included. */
    slot: { start: number; end: number };
    /** The ranges the signature covers, as written into the file. */
    byteRange: [number, number, number, number];
  };
  close: Record<string, never>;
}

export type WorkerOp = keyof WorkerRequestMap;

export interface WorkerRequest<Op extends WorkerOp = WorkerOp> {
  id: number;
  op: Op;
  args: WorkerRequestMap[Op];
}

export type WorkerResponse<Op extends WorkerOp = WorkerOp> =
  | { id: number; ok: true; result: WorkerResponseMap[Op] }
  | { id: number; ok: false; error: string };

// ─────────────────────────── page sizes ───────────────────────────

/** A4 portrait in PDF points. */
export const A4 = { width: 595.2756, height: 841.8898 } as const;

// ─────────────────────────── colour helpers ───────────────────────────

/** "#rrggbb" → [r, g, b] each 0..1 (what MuPDF setColor wants). */
export function hexToRgb01(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [1, 0.88, 0.4]; // fall back to the default yellow
  const n = parseInt(m[1]!, 16);
  return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
}

/** Axis-aligned bounding box of a quad — what the DOM overlay draws. */
export function quadBounds(quad: Quad): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const xs = [quad[0], quad[2], quad[4], quad[6]];
  const ys = [quad[1], quad[3], quad[5], quad[7]];
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}
